<template>
  <div>
    <!-- Edit mode with date/time pickers -->
    <v-row v-if="edit" no-gutters>
      <!-- Date picker -->
      <v-col :class="isDateTime ? 'pr-2' : ''">
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :ref="refKey"
              solo
              dense
              clearable
              :append-outer-icon="isDateTime ? null : 'mdi-delete-outline'"
              prepend-icon="mdi-calendar"
              v-model="dateValue"
              @click:append-outer="onDelete"
              @click:prepend="dateMenu = true"
              :rules="[rules.notEmpty]"
              v-bind="attrs"
              v-on="on"
            >
              <!-- Append Undo Button with Tooltip -->
              <template v-slot:append>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" @click="onUndo">
                      mdi-undo
                    </v-icon>
                  </template>
                  <span>{{ undoLiteral.value || 'blank' }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="dateValue"
            @input="onDateChange"
            no-title
            scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="secondary" @click="dateMenu = false">
              Cancel
            </v-btn>
            <v-btn text color="secondary" @click="dateMenu = false"> OK </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>

      <!-- Time picker for dateTime type -->
      <v-col v-if="isDateTime">
        <v-menu
          v-model="timeMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              solo
              dense
              append-outer-icon="mdi-delete-outline"
              prepend-icon="mdi-clock-outline"
              v-model="timeValue"
              readonly
              @click:append-outer="onDelete"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-model="timeValue"
            @input="onTimeChange"
            format="24hr"
            scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="secondary" @click="timeMenu = false">
              Cancel
            </v-btn>
            <v-btn text color="secondary" @click="timeMenu = false"> OK </v-btn>
          </v-time-picker>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Display mode with tooltip showing raw ISO 8601 string -->
    <div v-else class="d-inline-flex align-center">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs">
            <span class="text-caption">
              date
              <highlighted-text
                class="text-body-1"
                :value="displayDate"
                :highlights="highlights"
              ></highlighted-text
              >&nbsp;
            </span>
            <span v-if="isDateTime" class="text-caption">
              time
              <highlighted-text
                class="text-body-1"
                :value="displayTime"
                :highlights="highlights"
              ></highlighted-text>
            </span>
          </span>
        </template>
        <span>{{ 'raw ' + literal.value }}</span>
      </v-tooltip>
      <copy-icon :value="literal.value" />
    </div>
  </div>
</template>

<script>
import HighlightedText from './HighlightedText.vue'
import CopyIcon from './CopyIcon.vue'
import { Iris, Literal } from '@/rec-cache/rec-cache-types'

/**
 * DateTimeValue Component - Handles xsd:dateTime and xsd:date properties
 *
 * TIMEZONE HANDLING STRATEGY:
 * ---------------------------
 * Storage: All datetimes stored as UTC in ISO 8601 format (e.g., "2024-01-15T13:00:00Z")
 * Input: User enters time in their LOCAL timezone via the pickers
 * Output: Displays time converted back to user's LOCAL timezone
 *
 * ROUND-TRIP FLOW:
 * ----------------
 * 1. User enters "14:00" in CET timezone (local time)
 * 2. emitCombinedValue() creates Date object treating it as local: new Date("2024-01-15T14:00:00")
 * 3. toISOString() converts to UTC: "2024-01-15T13:00:00Z" (14:00 CET = 13:00 UTC)
 * 4. Backend stores: "2024-01-15T13:00:00Z"
 * 5. Display reads stored value via parseDateTimeValue()
 * 6. new Date("2024-01-15T13:00:00Z") parses as UTC, browser converts to local
 * 7. displayTime shows: "14:00" (13:00 UTC → 14:00 CET)
 * 8. Tooltip shows raw: "raw 2024-01-15T13:00:00Z"
 *
 * IMPORTANT: The Date() constructor and toISOString() handle all timezone conversions
 * automatically based on the browser's timezone settings. No manual offset calculation needed.
 */

export default {
  components: { HighlightedText, CopyIcon },
  props: {
    literal: {
      type: Object,
      default: () => new Literal(),
    },
    highlights: {
      type: [String, Array],
      default: () => [],
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dateValue: '',
    timeValue: '00:00',
    dateMenu: false,
    timeMenu: false,
    undoLiteral: new Literal(),
    rules: {
      notEmpty: (value) => !!value || 'Value must be defined!',
      validDateTime: (value) => {
        if (!value) return true
        const parsed = new Date(value)
        return !isNaN(parsed.getTime()) || 'Invalid date/time format!'
      },
    },
    refKey: '',
  }),
  computed: {
    isDateTime: function () {
      return this.literal.datatype === Iris.xsdDateTime
    },
    isDate: function () {
      return this.literal.datatype === Iris.xsdDate
    },
    // Display date in localized format
    displayDate: function () {
      if (!this.literal.value) return ''
      try {
        const date = new Date(this.literal.value)
        if (isNaN(date.getTime())) return this.literal.value
        return date.toLocaleDateString()
      } catch (error) {
        return this.literal.value
      }
    },
    // Display time in localized format (for dateTime only)
    displayTime: function () {
      if (!this.literal.value) return ''
      try {
        const date = new Date(this.literal.value)
        if (isNaN(date.getTime())) return this.literal.value
        return date.toLocaleTimeString()
      } catch (error) {
        return this.literal.value
      }
    },
  },
  watch: {
    literal: {
      handler(newValue) {
        this.parseDateTimeValue(newValue.value)
      },
      immediate: true,
    },
    edit: function (newValue) {
      if (newValue) {
        this.undoLiteral = this.literal.clone()
        this.autoFocus()
      }
    },
  },
  created: function () {
    this.parseDateTimeValue(this.literal.value)
    this.refKey = Math.random().toString(36).slice(2, 8)
  },
  mounted() {
    this.autoFocus()
  },
  methods: {
    parseDateTimeValue(value) {
      // Parses stored UTC datetime and extracts LOCAL date/time for the pickers
      // This ensures editing shows the same local time the user originally entered
      if (!value) {
        this.dateValue = ''
        this.timeValue = '00:00'
        return
      }

      try {
        const date = new Date(value) // Parses UTC, creates Date object in local timezone
        if (!isNaN(date.getTime())) {
          // Extract LOCAL date in YYYY-MM-DD format
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          this.dateValue = `${year}-${month}-${day}`

          // Extract LOCAL time in HH:MM format
          if (this.isDateTime) {
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            this.timeValue = `${hours}:${minutes}`
          }
        }
      } catch (error) {
        console.error('Error parsing date/time:', error)
      }
    },
    onDateChange() {
      this.dateMenu = false
      this.emitCombinedValue()
    },
    onTimeChange() {
      this.timeMenu = false
      this.emitCombinedValue()
    },
    emitCombinedValue() {
      // Combine date and time into ISO 8601 format, converting local time to UTC
      let value
      if (this.isDateTime) {
        if (this.dateValue && this.timeValue) {
          // Parse as local time, then convert to UTC
          const localDateTime = new Date(
            `${this.dateValue}T${this.timeValue}:00`
          )
          if (!isNaN(localDateTime.getTime())) {
            // toISOString() gives us UTC in ISO 8601 format with Z suffix
            value = localDateTime.toISOString()
          }
        }
      } else {
        // For date-only, just store the date (no timezone conversion needed)
        value = this.dateValue || ''
      }
      if (value) {
        this.$emit('input', new Literal(value, this.literal.datatype))
      }
    },
    autoFocus() {
      this.$nextTick(() => {
        if (!this.dateValue && this.$refs[this.refKey]) {
          this.$refs[this.refKey].focus()
        }
      })
    },
    onUndo: function () {
      this.$emit('input', this.undoLiteral)
    },
    onDelete: function () {
      this.$emit('delete', this.literal)
    },
  },
}
</script>

<style scoped>
/* Match the styling from LiteralValue */
</style>
